import { Link, useNavigate } from 'react-router-dom'
import { Moon, Sun, Monitor, LogIn, LogOut, Shield, Search, X, ExternalLink } from 'lucide-react'
import { useTheme } from '@/contexts/ThemeContext'
import { useAuth } from '@/contexts/AuthContext'
import { Button, Badge, Input } from '@/components/ui'
import { useState, useRef, useEffect } from 'react'
import { tools } from '@/pages/Tools'

export function Header() {
  const { theme, setTheme } = useTheme()
  const { user, isAuthenticated, logout } = useAuth()
  const navigate = useNavigate()
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)
  const searchDropdownRef = useRef<HTMLDivElement>(null)

  // Close search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target as Node) &&
          !searchInputRef.current?.contains(event.target as Node)) {
        setSearchOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Filter tools based on search query
  const searchResults = tools.filter(tool => {
    const query = searchQuery.toLowerCase()
    return tool.title.toLowerCase().includes(query) ||
           tool.description.toLowerCase().includes(query) ||
           tool.category.toLowerCase().includes(query)
  }).slice(0, 8) // Limit to 8 results

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      navigate(`/tools?q=${encodeURIComponent(searchQuery.trim())}`)
      setSearchOpen(false)
      setSearchQuery('')
    }
  }

  const toggleSearch = () => {
    setSearchOpen(!searchOpen)
    if (!searchOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 100)
    }
  }

  const cycleTheme = () => {
    const themes: Array<'dark' | 'light' | 'system'> = ['dark', 'light', 'system']
    const currentIndex = themes.indexOf(theme)
    const nextIndex = (currentIndex + 1) % themes.length
    setTheme(themes[nextIndex])
  }

  const ThemeIcon = {
    dark: Moon,
    light: Sun,
    system: Monitor,
  }[theme]

  const getRoleBadge = () => {
    if (!user) return null
    switch (user.role) {
      case 'admin':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs">Admin</Badge>
      case 'member':
        return <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-xs">Member</Badge>
      default:
        return <Badge variant="secondary" className="text-xs">User</Badge>
    }
  }

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-6 md:px-8">
      <div className="flex items-center gap-4 md:ml-0 ml-12">
        <div className="hidden md:flex items-center gap-2 text-sm text-muted-foreground">
          <span className="text-primary">{isAuthenticated ? user?.username : 'guest'}@scb</span>
          <span>:</span>
          <span className="text-foreground">~</span>
          <span className="terminal-cursor" />
        </div>
      </div>

      <div className="flex items-center gap-3">
        {/* Search */}
        <div className="relative">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSearch}
            className="relative"
          >
            <Search className="h-5 w-5" />
            <span className="sr-only">Search</span>
          </Button>

          {searchOpen && (
            <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" onClick={() => setSearchOpen(false)}>
              <div
                ref={searchDropdownRef}
                className="absolute right-0 md:right-8 top-16 w-full max-w-md md:w-80 bg-card border border-border rounded-lg shadow-xl max-h-[70vh] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <form onSubmit={handleSearchSubmit} className="p-3 border-b border-border">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search tools..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-10"
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </form>

                {searchQuery && searchResults.length > 0 ? (
                  <div className="max-h-[55vh] overflow-y-auto custom-scrollbar p-2">
                    {searchResults.map((tool) => {
                      const Icon = tool.icon
                      return (
                        <Link
                          key={tool.href}
                          to={tool.href}
                          onClick={() => {
                            setSearchOpen(false)
                            setSearchQuery('')
                          }}
                          className="flex items-center gap-3 px-3 py-2 hover:bg-accent transition-colors"
                        >
                          <div className={`flex h-8 w-8 items-center justify-center rounded-lg shrink-0 ${
                            tool.category === 'Special Tools' ? 'bg-gradient-to-br from-primary/20 to-purple-500/20' :
                            tool.category === 'Encryptors' ? 'bg-gradient-to-br from-orange-500/20 to-red-500/20' :
                            tool.category === 'Developer Tools' ? 'bg-gradient-to-br from-blue-500/20 to-cyan-500/20' :
                            'bg-primary/10'
                          }`}>
                            <Icon className={`h-4 w-4 ${
                              tool.category === 'Encryptors' ? 'text-orange-500' :
                              tool.category === 'Developer Tools' ? 'text-blue-500' :
                              'text-primary'
                            }`} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate">{tool.title}</p>
                            <p className="text-xs text-muted-foreground truncate">{tool.category}</p>
                          </div>
                          <ExternalLink className="h-3 w-3 text-muted-foreground shrink-0" />
                        </Link>
                      )
                    })}
                  </div>
                ) : searchQuery ? (
                  <div className="p-8 text-center text-sm text-muted-foreground">
                    No tools found matching "{searchQuery}"
                  </div>
                ) : (
                  <div className="p-8 text-center text-sm text-muted-foreground">
                    Start typing to search for tools...
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {isAuthenticated ? (
          <>
            <div className="hidden sm:flex items-center gap-2">
              {getRoleBadge()}
            </div>
            {user?.role === 'admin' && (
              <Link to="/admin">
                <Button variant="ghost" size="icon" className="relative">
                  <Shield className="h-5 w-5 text-red-400" />
                </Button>
              </Link>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => { await logout(); window.location.href = '/auth' }}
              className="text-muted-foreground hover:text-foreground"
            >
              <LogOut className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </>
        ) : (
          <Link to="/auth">
            <Button variant="ghost" size="sm" className="text-primary">
              <LogIn className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Sign In</span>
            </Button>
          </Link>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={cycleTheme}
          className="relative"
        >
          <ThemeIcon className="h-5 w-5" />
          <span className="sr-only">Toggle theme</span>
        </Button>
      </div>
    </header>
  )
}
